/**
 * Compliance Data - Sample compliance data for regulatory frameworks
 *
 * @description Mock data for AIVSS metrics, regulatory mappings, custom frameworks,
 *              and company policies. In production, this should be fetched from API.
 * @copyright 2026 Guardrail Technologies
 */

import type {
  AIVSSMetric,
  RegulatoryMapping,
  CustomFramework,
  CompanyPolicy,
  PrintSettings,
} from '@/types/compliance';

/**
 * AIVSS Base Metrics mapping to regulatory frameworks
 * These metrics represent the security and risk assessment scores
 */
export const aivssMetrics: AIVSSMetric[] = [
  {
    id: 'data-sensitivity',
    name: 'Data Sensitivity',
    value: 6.2,
    maxValue: 10,
    description: 'Measures the sensitivity of data processed by the AI system',
  },
  {
    id: 'model-robustness',
    name: 'Model Robustness',
    value: 5.5,
    maxValue: 10,
    description: 'Evaluates the model resistance to adversarial attacks',
  },
  {
    id: 'decision-criticality',
    name: 'Decision Criticality',
    value: 7.8,
    maxValue: 10,
    description: 'Assesses the impact of AI decisions on individuals',
  },
  {
    id: 'autonomy-level',
    name: 'Autonomy Level',
    value: 6.5,
    maxValue: 10,
    description: 'Degree of independent decision-making',
  },
  {
    id: 'transparency',
    name: 'Transparency',
    value: 4.2,
    maxValue: 10,
    description: 'Explainability and interpretability of AI decisions',
  },
  {
    id: 'human-oversight',
    name: 'Human Oversight',
    value: 5.8,
    maxValue: 10,
    description: 'Quality of human supervision mechanisms',
  },
  {
    id: 'bias-fairness',
    name: 'Bias & Fairness',
    value: 5.0,
    maxValue: 10,
    description: 'Measures discriminatory patterns and fairness',
  },
  {
    id: 'data-governance',
    name: 'Data Governance',
    value: 6.8,
    maxValue: 10,
    description: 'Quality of data management practices',
  },
  {
    id: 'security-controls',
    name: 'Security Controls',
    value: 7.2,
    maxValue: 10,
    description: 'Strength of security measures',
  },
  {
    id: 'accountability',
    name: 'Accountability',
    value: 5.5,
    maxValue: 10,
    description: 'Clear responsibility and liability frameworks',
  },
];

/**
 * Regulatory mappings between AIVSS metrics and compliance frameworks
 */
export const regulatoryMappings: RegulatoryMapping[] = [
  {
    metricId: 'data-sensitivity',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)', 'Art. 15 (Accuracy)'],
      requirements: ['Data quality requirements', 'Training data governance'],
      status: 'partial',
      gaps: ['Data provenance documentation incomplete'],
    },
    nistAIRMF: {
      functions: ['MAP', 'MEASURE'],
      categories: ['MAP 1.1', 'MEASURE 2.3'],
      status: 'compliant',
      gaps: [],
    },
  },
  {
    metricId: 'model-robustness',
    euAIAct: {
      articles: ['Art. 15 (Accuracy, Robustness, Cybersecurity)'],
      requirements: ['Robustness against errors', 'Cybersecurity measures'],
      status: 'partial',
      gaps: ['Adversarial testing documentation missing'],
    },
    nistAIRMF: {
      functions: ['MEASURE', 'MANAGE'],
      categories: ['MEASURE 2.7', 'MANAGE 2.3'],
      status: 'partial',
      gaps: ['Continuous monitoring not fully implemented'],
    },
  },
  {
    metricId: 'decision-criticality',
    euAIAct: {
      articles: ['Art. 6 (Classification Rules)', 'Annex III (High-Risk)'],
      requirements: ['Risk classification', 'High-risk system requirements'],
      status: 'non-compliant',
      gaps: ['Risk classification not formally documented', 'Impact assessment incomplete'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'],
      categories: ['GOVERN 1.3', 'MAP 2.1'],
      status: 'partial',
      gaps: ['Risk tolerance not defined'],
    },
  },
  {
    metricId: 'autonomy-level',
    euAIAct: {
      articles: ['Art. 14 (Human Oversight)'],
      requirements: ['Human-machine interface', 'Override capabilities'],
      status: 'partial',
      gaps: ['Override mechanisms need documentation'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MANAGE'],
      categories: ['GOVERN 1.5', 'MANAGE 3.1'],
      status: 'compliant',
      gaps: [],
    },
  },
  {
    metricId: 'transparency',
    euAIAct: {
      articles: ['Art. 13 (Transparency)', 'Art. 52 (Transparency Obligations)'],
      requirements: ['User notification', 'Explainability of decisions'],
      status: 'non-compliant',
      gaps: [
        'Lack of creator verification',
        'Explainability documentation missing',
        'User notification incomplete',
      ],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'],
      categories: ['GOVERN 1.4', 'MAP 1.5'],
      status: 'non-compliant',
      gaps: ['Transparency practices not documented'],
    },
  },
  {
    metricId: 'human-oversight',
    euAIAct: {
      articles: ['Art. 14 (Human Oversight)'],
      requirements: ['Appropriate human oversight', 'Intervention capabilities'],
      status: 'partial',
      gaps: ['Oversight procedures need formalization'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MANAGE'],
      categories: ['GOVERN 1.6', 'MANAGE 4.1'],
      status: 'compliant',
      gaps: [],
    },
  },
  {
    metricId: 'bias-fairness',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)', 'Recital 44 (Non-discrimination)'],
      requirements: ['Bias detection', 'Fairness testing'],
      status: 'partial',
      gaps: ['Bias testing documentation incomplete'],
    },
    nistAIRMF: {
      functions: ['MAP', 'MEASURE'],
      categories: ['MAP 2.3', 'MEASURE 2.10'],
      status: 'partial',
      gaps: ['Fairness metrics not fully defined'],
    },
  },
  {
    metricId: 'data-governance',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)'],
      requirements: ['Data management', 'Data quality criteria'],
      status: 'compliant',
      gaps: [],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'],
      categories: ['GOVERN 1.2', 'MAP 1.2'],
      status: 'compliant',
      gaps: [],
    },
  },
  {
    metricId: 'security-controls',
    euAIAct: {
      articles: ['Art. 15 (Cybersecurity)'],
      requirements: ['Cybersecurity measures', 'Resilience against attacks'],
      status: 'compliant',
      gaps: [],
    },
    nistAIRMF: {
      functions: ['MANAGE'],
      categories: ['MANAGE 2.4'],
      status: 'compliant',
      gaps: [],
    },
  },
  {
    metricId: 'accountability',
    euAIAct: {
      articles: ['Art. 16-27 (Provider Obligations)', 'Art. 9 (Risk Management)'],
      requirements: ['Quality management', 'Documentation requirements'],
      status: 'partial',
      gaps: ['Provider obligations documentation incomplete'],
    },
    nistAIRMF: {
      functions: ['GOVERN'],
      categories: ['GOVERN 1.1', 'GOVERN 1.7'],
      status: 'partial',
      gaps: ['Accountability structures need formalization'],
    },
  },
];

/**
 * Static regulatory mapping definitions — framework reference data (articles, requirements,
 * NIST functions/categories) stripped of per-scan status and gaps. These are factual regulatory
 * references that don't change per scan.
 */
export const regulatoryMappingDefinitions = [
  {
    metricId: 'data-sensitivity',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)', 'Art. 15 (Accuracy)'],
      requirements: ['Data quality requirements', 'Training data governance'],
    },
    nistAIRMF: {
      functions: ['MAP', 'MEASURE'] as const,
      categories: ['MAP 1.1', 'MEASURE 2.3'],
    },
  },
  {
    metricId: 'model-robustness',
    euAIAct: {
      articles: ['Art. 15 (Accuracy, Robustness, Cybersecurity)'],
      requirements: ['Robustness against errors', 'Cybersecurity measures'],
    },
    nistAIRMF: {
      functions: ['MEASURE', 'MANAGE'] as const,
      categories: ['MEASURE 2.7', 'MANAGE 2.3'],
    },
  },
  {
    metricId: 'decision-criticality',
    euAIAct: {
      articles: ['Art. 6 (Classification Rules)', 'Annex III (High-Risk)'],
      requirements: ['Risk classification', 'High-risk system requirements'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'] as const,
      categories: ['GOVERN 1.3', 'MAP 2.1'],
    },
  },
  {
    metricId: 'autonomy-level',
    euAIAct: {
      articles: ['Art. 14 (Human Oversight)'],
      requirements: ['Human-machine interface', 'Override capabilities'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MANAGE'] as const,
      categories: ['GOVERN 1.5', 'MANAGE 3.1'],
    },
  },
  {
    metricId: 'transparency',
    euAIAct: {
      articles: ['Art. 13 (Transparency)', 'Art. 52 (Transparency Obligations)'],
      requirements: ['User notification', 'Explainability of decisions'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'] as const,
      categories: ['GOVERN 1.4', 'MAP 1.5'],
    },
  },
  {
    metricId: 'human-oversight',
    euAIAct: {
      articles: ['Art. 14 (Human Oversight)'],
      requirements: ['Appropriate human oversight', 'Intervention capabilities'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MANAGE'] as const,
      categories: ['GOVERN 1.6', 'MANAGE 4.1'],
    },
  },
  {
    metricId: 'bias-fairness',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)', 'Recital 44 (Non-discrimination)'],
      requirements: ['Bias detection', 'Fairness testing'],
    },
    nistAIRMF: {
      functions: ['MAP', 'MEASURE'] as const,
      categories: ['MAP 2.3', 'MEASURE 2.10'],
    },
  },
  {
    metricId: 'data-governance',
    euAIAct: {
      articles: ['Art. 10 (Data Governance)'],
      requirements: ['Data management', 'Data quality criteria'],
    },
    nistAIRMF: {
      functions: ['GOVERN', 'MAP'] as const,
      categories: ['GOVERN 1.2', 'MAP 1.2'],
    },
  },
  {
    metricId: 'security-controls',
    euAIAct: {
      articles: ['Art. 15 (Cybersecurity)'],
      requirements: ['Cybersecurity measures', 'Resilience against attacks'],
    },
    nistAIRMF: {
      functions: ['MANAGE'] as const,
      categories: ['MANAGE 2.4'],
    },
  },
  {
    metricId: 'accountability',
    euAIAct: {
      articles: ['Art. 16-27 (Provider Obligations)', 'Art. 9 (Risk Management)'],
      requirements: ['Quality management', 'Documentation requirements'],
    },
    nistAIRMF: {
      functions: ['GOVERN'] as const,
      categories: ['GOVERN 1.1', 'GOVERN 1.7'],
    },
  },
] as const;

/**
 * Custom compliance frameworks (e.g., ISO 42001)
 */
export const customFrameworks: CustomFramework[] = [
  {
    id: 'iso-42001',
    name: 'ISO/IEC 42001:2023',
    description: 'AI Management System Standard',
    requirements: [
      {
        id: 'iso-4.1',
        name: 'Context of the Organization',
        description: 'Understanding the organization context for AI',
        mappedMetrics: ['accountability', 'data-governance'],
        status: 'compliant',
      },
      {
        id: 'iso-6.1',
        name: 'Risk Assessment',
        description: 'AI-specific risk assessment processes',
        mappedMetrics: ['decision-criticality', 'model-robustness'],
        status: 'partial',
      },
      {
        id: 'iso-7.2',
        name: 'Competence',
        description: 'Ensuring AI competence in the organization',
        mappedMetrics: ['human-oversight'],
        status: 'compliant',
      },
      {
        id: 'iso-9.1',
        name: 'Monitoring & Measurement',
        description: 'Performance monitoring of AI systems',
        mappedMetrics: ['transparency', 'bias-fairness'],
        status: 'partial',
      },
    ],
  },
];

/**
 * Company-specific AI governance policies
 */
export const companyPolicies: CompanyPolicy[] = [
  {
    id: 'internal-ai-policy',
    name: 'Internal AI Governance Policy',
    description: 'Company-specific AI usage requirements',
    requirements: [
      {
        id: 'pol-1',
        name: 'Data Sensitivity Threshold',
        threshold: 5.0,
        operator: 'lte',
        mappedMetric: 'data-sensitivity',
        status: 'non-compliant',
      },
      {
        id: 'pol-2',
        name: 'Human Oversight Minimum',
        threshold: 6.0,
        operator: 'gte',
        mappedMetric: 'human-oversight',
        status: 'non-compliant',
      },
      {
        id: 'pol-3',
        name: 'Transparency Score',
        threshold: 5.0,
        operator: 'gte',
        mappedMetric: 'transparency',
        status: 'non-compliant',
      },
      {
        id: 'pol-4',
        name: 'Security Controls Minimum',
        threshold: 7.0,
        operator: 'gte',
        mappedMetric: 'security-controls',
        status: 'compliant',
      },
    ],
  },
];

/**
 * Default print settings for compliance reports
 */
export const defaultPrintSettings: PrintSettings = {
  showHeader: true,
  showSummary: true,
  showEUAIAct: true,
  showNISTAIRMF: true,
  showCustomFrameworks: true,
  showCompanyPolicy: true,
  showComplianceGaps: true,
  showRegulatorySummary: true,
};
